import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getFCMToken, onForegroundMessage } from "@/lib/firebase";

type PushNotificationState = {
  isSupported: boolean;
  permission: NotificationPermission | null;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
};

type UsePushNotificationsOptions = {
  autoSubscribe?: boolean;
  autoPrompt?: boolean;
};

type ErrorWithResponse = Error & {
  response?: { data?: { message?: string } };
};

const focusedTabsStorageKey = "airie-focused-tabs";
const focusedTabHeartbeatMaxAgeMs = 10_000;

function readFocusedTabs(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(focusedTabsStorageKey) ?? "{}") as Record<
      string,
      number
    >;
  } catch {
    return {};
  }
}

function writeFocusedTabs(tabs: Record<string, number>) {
  localStorage.setItem(focusedTabsStorageKey, JSON.stringify(tabs));
}

function pruneFocusedTabs(tabs: Record<string, number>, now = Date.now()) {
  return Object.fromEntries(
    Object.entries(tabs).filter(([, timestamp]) => now - timestamp <= focusedTabHeartbeatMaxAgeMs),
  );
}

function hasFocusedAirieTab() {
  return Object.keys(pruneFocusedTabs(readFocusedTabs())).length > 0;
}

function isIOS() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isIOSPushSupported() {
  if (!isIOS()) {
    return true;
  }

  const match = navigator.userAgent.match(/OS (\d+)[._](\d+)/);
  if (!match) {
    return false;
  }

  const major = Number(match[1]);
  const minor = Number(match[2]);
  return major > 16 || (major === 16 && minor >= 4);
}

function isPWAInstalled() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function getDeviceInfo() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    standalone: isPWAInstalled(),
  };
}

function pushSubscriptionUrl() {
  const workspacePrefix = window.location.pathname.match(/^\/w\/[^/]+/)?.[0] ?? "";
  return `${workspacePrefix}/push-subscriptions`;
}

function notificationTargetUrl(data: Record<string, string> | undefined) {
  if (!data) {
    return null;
  }

  if (data.url || data.click_action) {
    return data.url || data.click_action;
  }

  if (data.type === "new_message" && data.conversation_id && data.subdomain) {
    return `/w/${data.subdomain}/channels/${data.conversation_id}`;
  }

  return null;
}

export function usePushNotifications(options: UsePushNotificationsOptions = {}) {
  const { autoSubscribe = true, autoPrompt = false } = options;
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: null,
    isSubscribed: false,
    isLoading: false,
    error: null,
  });

  const requestPermissionRef = useRef<(() => Promise<boolean>) | null>(null);
  const hasAutoSubscribedRef = useRef(false);
  const hasAutoPromptedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const tabId = crypto.randomUUID();
    const updateFocusedTab = (forceRemove = false) => {
      const tabs = pruneFocusedTabs(readFocusedTabs());
      if (!forceRemove && document.visibilityState === "visible" && document.hasFocus()) {
        tabs[tabId] = Date.now();
      } else {
        delete tabs[tabId];
      }
      writeFocusedTabs(tabs);
    };

    const handleFocusChange = () => updateFocusedTab();

    updateFocusedTab();
    const intervalId = window.setInterval(handleFocusChange, 5_000);
    document.addEventListener("visibilitychange", handleFocusChange);
    window.addEventListener("focus", handleFocusChange);
    window.addEventListener("blur", handleFocusChange);
    const handlePageHide = () => updateFocusedTab(true);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleFocusChange);
      window.removeEventListener("focus", handleFocusChange);
      window.removeEventListener("blur", handleFocusChange);
      window.removeEventListener("pagehide", handlePageHide);
      const tabs = readFocusedTabs();
      delete tabs[tabId];
      writeFocusedTabs(pruneFocusedTabs(tabs));
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setState((previous) => ({ ...previous, isSupported: false }));
      return;
    }

    if (isIOS() && !isIOSPushSupported()) {
      setState((previous) => ({
        ...previous,
        isSupported: false,
        error: "Las notificaciones push requieren iOS 16.4 o posterior",
      }));
      return;
    }

    if (isIOS() && !isPWAInstalled()) {
      setState((previous) => ({
        ...previous,
        isSupported: false,
        error: "Agrega Airie a tu pantalla de inicio para recibir notificaciones push",
      }));
      return;
    }

    setState((previous) => ({
      ...previous,
      isSupported: true,
      permission: Notification.permission,
    }));
  }, []);

  useEffect(() => {
    if (!state.isSupported || state.permission !== "granted") {
      return;
    }

    const unsubscribe = onForegroundMessage((payload) => {
      if (hasFocusedAirieTab()) {
        return;
      }

      const data = payload.data;
      const title =
        payload.notification?.title || data?.title || data?.conversation_name || "Airie";
      const body = payload.notification?.body || data?.body || "Nueva notificación";
      const notification = new Notification(title, {
        body,
        icon: payload.notification?.icon || "/pwa-icons/icon-192.png",
        badge: "/pwa-icons/notification-badge.png",
        data,
        tag: data?.conversation_id ? `conversation:${data.conversation_id}` : undefined,
      });

      notification.addEventListener("click", () => {
        const url = notificationTargetUrl(data);
        if (url) {
          window.focus();
          window.location.assign(url);
        }
        notification.close();
      });
    });

    return () => {
      unsubscribe?.();
    };
  }, [state.isSupported, state.permission]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      toast.error(state.error || "Las notificaciones push no están disponibles");
      return false;
    }

    if (state.permission === "granted") {
      return true;
    }

    setState((previous) => ({ ...previous, isLoading: true, error: null }));

    try {
      const permission = await Notification.requestPermission();
      setState((previous) => ({ ...previous, permission, isLoading: false }));

      if (permission === "granted") {
        return true;
      }

      if (permission === "denied") {
        setState((previous) => ({
          ...previous,
          error: "Los permisos de notificación fueron denegados",
        }));
      }

      return false;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      setState((previous) => ({
        ...previous,
        isLoading: false,
        error: "Error al solicitar permisos de notificación",
      }));
      return false;
    }
  }, [state.error, state.isSupported, state.permission]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (Notification.permission !== "granted") {
      const granted = await requestPermission();
      if (!granted) {
        return false;
      }
    }

    setState((previous) => ({ ...previous, isLoading: true, error: null }));

    try {
      const token = await getFCMToken();
      if (!token) {
        setState((previous) => ({
          ...previous,
          isLoading: false,
          error: "No se pudo obtener el token de FCM",
        }));
        return false;
      }

      await axios.post(pushSubscriptionUrl(), {
        fcm_token: token,
        device_info: getDeviceInfo(),
      });

      setState((previous) => ({ ...previous, isSubscribed: true, isLoading: false }));
      return true;
    } catch (error) {
      console.error("Error subscribing to push notifications:", error);
      const typedError = error as ErrorWithResponse;
      const errorMessage =
        typedError.response?.data?.message ||
        typedError.message ||
        "Error al suscribirse a las notificaciones";
      setState((previous) => ({ ...previous, isLoading: false, error: errorMessage }));
      return false;
    }
  }, [requestPermission]);

  useEffect(() => {
    requestPermissionRef.current = requestPermission;
  }, [requestPermission]);

  useEffect(() => {
    if (
      !autoSubscribe ||
      hasAutoSubscribedRef.current ||
      !state.isSupported ||
      state.permission !== "granted" ||
      state.isLoading
    ) {
      return;
    }

    hasAutoSubscribedRef.current = true;
    subscribe().catch((error) => console.error("Auto-subscription failed:", error));
  }, [autoSubscribe, state.isLoading, state.isSupported, state.permission, subscribe]);

  useEffect(() => {
    if (
      !autoPrompt ||
      hasAutoPromptedRef.current ||
      !state.isSupported ||
      state.permission !== "default" ||
      state.isLoading
    ) {
      return;
    }

    const hasPromptedThisSession = sessionStorage.getItem("push-notification-prompted");
    if (hasPromptedThisSession === "true") {
      hasAutoPromptedRef.current = true;
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      hasAutoPromptedRef.current = true;
      sessionStorage.setItem("push-notification-prompted", "true");
      await requestPermissionRef.current?.();
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [autoPrompt, state.isLoading, state.isSupported, state.permission]);

  return {
    ...state,
    requestPermission,
    subscribe,
  };
}
